import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext } from "../../context/AuthContextProvider";
import { loginPassword } from "../../api/auth";
import {
  ANALYTICS_EVENTS,
  LOGIN_METHOD,
  isFirstLogin,
  logAnalyticsEvent,
} from "../../analytics";

import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Divider,
} from "@mui/material";



const loginhome = `${process.env.PUBLIC_URL}/assets/login-home.svg`;
import spandanOneLogo from "../../assets/images/logos/spandan-logo-2.svg";
import google from "../../assets/images/icons/devicon_google.svg";
import linkedin from "../../assets/images/icons/devicon_linkedin.svg";
import CountryCodePicker from "../../components/CountryCodePicker";
import LegalLinks from "../../components/LegalLinks";
import {
  getPhoneNumberLimit,
  getPhoneNumberLimitMessage,
  sanitizePhoneNumber,
} from "../../utils/phoneNumber";

const LoginPassword = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState("phone");

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const phoneLimit = getPhoneNumberLimit(countryCode);
  const isPhoneValid = phoneNumber.length === phoneLimit;

  useEffect(() => {
    if (location.state?.phoneNumber) {
      setPhoneNumber(location.state.phoneNumber);
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = async () => {
    if (loginType === "phone" && !isPhoneValid) {
      toast.error(getPhoneNumberLimitMessage(countryCode));
      return;
    }

    if (loginType === "virtual" && phoneNumber.length < 4) {
      toast.error("Enter a valid Virtual ID");
      return;
    }

    if (!password) {
      toast.error("Password is required");
      return;
    }

    setLoading(true);

    try {
      const payload =
        loginType === "phone"
          ? {
            phoneNumber: countryCode + phoneNumber,
            password,
            platform: "web",
          }
          : {
            virtualId: phoneNumber,
            password,
            platform: "web",
          };

      const res = await loginPassword(payload);

      if (!res.success) {
        logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
          login_method: LOGIN_METHOD.PASSWORD,
          failure_reason: res.message,
        });
        toast.error(res.message);
      } else {
        toast.success("Login successful");
        logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
          login_method: LOGIN_METHOD.PASSWORD,
          is_first_login: isFirstLogin(res.data),
        });
        login(res.data, res.data.authorization);
      }
    } catch (err) {
      logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
        login_method: LOGIN_METHOD.PASSWORD,
        failure_reason: "request_threw",
      });
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Spandan One</title>
      </Helmet>

      <Box sx={{ minHeight: "100vh", display: "flex", backgroundColor: "var(--app-surface)" }}>
        {/* LEFT IMAGE */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: "none", md: "flex" },
            alignItems: "start",
            justifyContent: "start",
            height: "100vh",
            position: "sticky",
            top: 0,
            overflow: "hidden",
          }}
        >
          <img
            src={loginhome}
            alt="login"
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "left top",
            }}
          />
        </Box>

        {/* RIGHT LOGIN CARD */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            px: { xs: 2.5, sm: 3 },
            py: { xs: 5, md: 4 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              my: "auto",
              maxWidth: { xs: "100%", sm: 420, md: "min(80%, 780px)" },
              mx: { xs: "auto", md: 0 },
            }}
          >
            {/* BRAND LOGO (mobile only) */}
            <Box
              component="img"
              src={spandanOneLogo}
              alt="Spandan One"
              sx={{
                height: { xs: 72, sm: 98 },
                mb: "24px",
                mx: "auto",
                display: { xs: "block", md: "none" },
              }}
            />

            {/* Title */}
            <Typography
              sx={{
                fontSize: { xs: "22px !important", sm: "26px !important" },
                fontWeight: 700,
                color: "var(--app-text)",
                mb: 3,
                fontFamily: "Figtree",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Login to your Account
            </Typography>
            <Box sx={{ display: "flex", borderBottom: "1px solid var(--app-border)", mb: 3 }}>
              <Box
                onClick={() => {
                  setLoginType("phone");
                  setPhoneNumber("");
                }}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  pb: 1,
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: { xs: 14, sm: 16 },
                  color: loginType === "phone" ? "var(--app-text)" : "var(--app-text-muted)",
                  borderBottom: loginType === "phone" ? "2px solid #5B5BE0" : "none",
                }}
              >
                Mobile Number
              </Box>

              <Box
                onClick={() => {
                  setLoginType("virtual");
                  setPhoneNumber("");
                }}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  pb: 1,
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: { xs: 14, sm: 16 },
                  color: loginType === "virtual" ? "var(--app-text)" : "var(--app-text-muted)",
                  borderBottom: loginType === "virtual" ? "2px solid #5B5BE0" : "none",
                }}
              >
                User ID
              </Box>
            </Box>

            {/* Mobile Number */}
            <Typography sx={{ fontSize: 14, color: "var(--app-text-muted)", mb: 0.5 }}>
              {loginType === "phone" ? "Mobile Number" : "User ID"}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                value={phoneNumber}
                onChange={(e) => {
                  const v = e.target.value;

                  if (loginType === "phone") {
                    if (/^\d{0,10}$/.test(v)) setPhoneNumber(v);
                  } else {
                    setPhoneNumber(v); // no limit for user ID
                  }
                }}
                inputProps={
                  loginType === "phone"
                    ? { maxLength: 10 }
                    : {}
                }
                placeholder={
                  loginType === "phone"
                    ? "Enter your phone number"
                    : "Enter User ID"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 48,
                    borderRadius: "10px",
                    backgroundColor: "var(--app-surface)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid var(--app-border)",
                    },
                  },
                }}
              />
            </Box>

            {/* Password */}
            <Typography sx={{ fontSize: 14, color: "var(--app-text-muted)", mb: 0.5 }}>
              Password
            </Typography>

            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  height: 48,
                  borderRadius: "10px",
                  backgroundColor: "var(--app-surface)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  border: "1px solid var(--app-border)",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      component="img"
                      src="https://cdn-icons-png.flaticon.com/512/159/159604.png"
                      alt="eye"
                      sx={{
                        width: 18,
                        cursor: "pointer",
                        opacity: 0.6,
                      }}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* Login Button */}
            <Button
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              sx={{
                height: 46,
                borderRadius: "10px",
                backgroundColor: "#5B5BE0",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#4C4CC9",
                },
                "&:disabled": {
                  backgroundColor: "var(--app-border)",
                  color: "var(--app-text-muted)",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                  Logging in
                </>
              ) : (
                "Login"
              )}
            </Button>

            {/* Links */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 3,
                fontSize: 14,
              }}
            >
              {/* <Typography sx={{ color: "var(--app-text-muted)" }}>
                Forgot your password?{" "}
                <Link to="/reset-password" style={{ color: "#5B5BE0" }}>
                  Reset Password
                </Link>
              </Typography> */}

              <Link to="/login" state={{ phoneNumber }} style={{ color: "#5B5BE0", fontWeight: 700 }}>
                Login via OTP
              </Link>
            </Box>

            {/* Divider */}
            {/* <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--app-border)" }} />
              <Typography sx={{ px: 2, fontSize: 14, color: "var(--app-text-muted)" }}>
                or
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--app-border)" }} />
            </Box> */}

            {/* Social Login */}
            {/* <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
              {[google, linkedin].map((icon, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: "1px solid var(--app-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <img src={icon} alt="social" width={20} />
                </Box>
              ))}
            </Box> */}
          </Box>

          <LegalLinks />
        </Box>
      </Box>
    </>
  );
};

export default LoginPassword;
